import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiActivity, FiGlobe, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const Hero = () => {
  return (
    <section className="relative pt-32 pb-20 overflow-hidden border-b border-white/5">
      {/* Background Decorative Elements */}
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#CCFF00]/5 blur-[120px] -z-10 pointer-events-none rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="flex items-center gap-2 px-3 py-1 bg-[#CCFF00]/10 border border-[#CCFF00]/20 rounded-full">
                <div className="w-1.5 h-1.5 bg-[#CCFF00] rounded-full animate-pulse"></div>
                <span className="text-[#CCFF00] text-[10px] font-black uppercase tracking-widest">Global Network Online</span>
              </div>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter uppercase leading-[0.9] mb-10"
            >
              The latest <br />
              <span className="text-transparent" style={{ WebkitTextStroke: '1.5px #CCFF00' }}>Football</span> news to <br />
              your local users <br />
              <span className="text-[#CCFF00]">— effortlessly</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-slate-400 text-lg md:text-xl max-w-2xl leading-relaxed mb-12 font-medium"
            >
              High-performance football reporting delivered in <span className="text-white">40+ languages</span>. We empower global sports platforms with rapid, localized news feeds that resonate with every audience.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-5"
            >
              <Link 
                to="/news" 
                className="bg-[#CCFF00] text-black px-10 py-5 text-[11px] font-black uppercase tracking-[0.2em] hover:bg-white transition-all flex items-center justify-center gap-3 group"
              >
                Read the latest news
                <SafeIcon icon={FiActivity} className="text-lg group-hover:scale-110 transition-transform" />
              </Link>
              
              <a 
                href="#contact" 
                className="bg-white/5 text-white border border-white/10 px-10 py-5 text-[11px] font-black uppercase tracking-[0.2em] hover:bg-white/10 transition-all flex items-center justify-center gap-3"
              >
                Partner with us
                <SafeIcon icon={FiArrowRight} />
              </a>
            </motion.div>
          </div>

          {/* Globe Visualization */}
          <div className="lg:col-span-4 hidden lg:flex justify-center items-center relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative w-full aspect-square max-w-[350px]"
            >
              {/* Outer Rings */}
              <div className="absolute inset-0 border border-[#CCFF00]/20 rounded-full animate-[spin_20s_linear_infinite]"></div>
              <div className="absolute inset-4 border border-white/5 rounded-full animate-[spin_15s_linear_infinite_reverse]"></div>
              
              {/* Central Globe Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                    className="text-[#CCFF00]/10"
                  >
                    <SafeIcon icon={FiGlobe} className="text-[240px]" />
                  </motion.div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <SafeIcon icon={FiGlobe} className="text-white text-8xl blur-[2px] opacity-20" />
                    <SafeIcon icon={FiGlobe} className="absolute text-[#CCFF00] text-8xl" />
                  </div>
                </div>
              </div>

              {/* Ping Points */}
              <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-[#CCFF00] rounded-full shadow-[0_0_15px_#CCFF00] animate-pulse"></div>
              <div className="absolute bottom-1/3 right-1/4 w-2 h-2 bg-[#CCFF00] rounded-full shadow-[0_0_15px_#CCFF00] animate-pulse delay-700"></div>
              <div className="absolute top-1/2 right-10 w-2 h-2 bg-[#CCFF00] rounded-full shadow-[0_0_15px_#CCFF00] animate-pulse delay-1000"></div>
            </motion.div>
          </div>
        </div>

        {/* Global Reach Ticker */}
        <div className="mt-24 pt-8 border-t border-white/5 flex flex-wrap gap-12">
          {[
            { label: 'Coverage', value: 'Global Reach', icon: FiGlobe },
            { label: 'Localization', value: '40+ Languages', icon: FiCheck },
            { label: 'Reliability', value: 'Verified Agency', icon: FiCheck }
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <SafeIcon icon={stat.icon} className="text-[#CCFF00] text-xs" />
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{stat.label}</span>
              </div>
              <span className="text-white font-mono-data text-xl font-bold uppercase tracking-tighter">{stat.value}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;